import { lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
// Home is eagerly loaded — it is the LCP-critical first route.
import Home from "@/pages/Home";
// All other page components load on demand so the initial JS bundle stays small.
const Browse             = lazy(() => import("@/pages/Browse"));
const Cart               = lazy(() => import("@/pages/Cart"));
const SellerPage         = lazy(() => import("@/pages/SellerPage"));
const SellerDashboard    = lazy(() => import("@/pages/SellerDashboard"));
const SupportPage        = lazy(() => import("@/pages/SupportPage"));
const AdminHub           = lazy(() => import("@/pages/AdminSupportPage"));
const AdminSupportPage   = lazy(() => import("@/pages/AdminSupportPage"));
const MemberSettingsPage = lazy(() => import("@/pages/MemberSettingsPage"));
const Terms              = lazy(() => import("@/pages/Terms"));
const Verify             = lazy(() => import("@/pages/Verify"));
const NotFound           = lazy(() => import("@/pages/NotFound"));
const GearFinder         = lazy(() => import("@/pages/GearFinder"));
const ListingPage        = lazy(() => import("@/pages/ListingPage"));
const TubeFamilyPage     = lazy(() => import("@/pages/TubeFamilyPage"));
const AmpGuidePage       = lazy(() => import("@/pages/AmpGuidePage"));
const AmpBrandIndexPage  = lazy(() => import("@/pages/AmpBrandIndexPage"));
const AllAmpBrandsPage   = lazy(() => import("@/pages/AllAmpBrandsPage"));
const EIALookupPage      = lazy(() => import("@/pages/EIALookupPage"));
const QuickStartGuide    = lazy(() => import("@/pages/QuickStartGuide"));
const UserGuide          = lazy(() => import("@/pages/UserGuide"));
const AboutPage          = lazy(() => import("@/pages/AboutPage"));
const EditorialPolicyPage = lazy(() => import("@/pages/EditorialPolicyPage"));
const BuyListPage        = lazy(() => import("@/pages/BuyListPage"));
const VerifyEmail        = lazy(() => import("@/pages/VerifyEmail"));
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { useAuth } from "@/hooks/useAuth";
import { TubeDealerAssistant } from "@/components/TubeDealerAssistant";
import { useRouteTracker } from "@/hooks/useRouteTracker";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// Fires a GA4 page_view after every Wouter route change, once the title is set.
// Must be rendered inside <WouterRouter> so useLocation has router context.
function RouteTracker() {
  useRouteTracker();
  return null;
}

// Mounts the floating assistant only when a user is signed in
function AssistantLayer() {
  const { user } = useAuth();
  if (!user) return null;
  return <TubeDealerAssistant username={user.username} />;
}

// Minimal spinner shown while a lazy page chunk is loading.
// Keeps the existing dark theme and avoids layout shift.
function PageLoadingFallback() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        color: "#555",
        fontSize: 14,
      }}
    >
      Loading…
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/browse" component={Browse} />
        <Route path="/listing/:id" component={ListingPage} />
        <Route path="/gear/:id" component={GearFinder} />
        <Route path="/gear" component={GearFinder} />
        <Route path="/tubes/:familySlug" component={TubeFamilyPage} />
        <Route path="/amps/:brandSlug/:modelSlug" component={AmpGuidePage} />
        <Route path="/amps/:brandSlug" component={AmpBrandIndexPage} />
        <Route path="/amps" component={AllAmpBrandsPage} />
        <Route path="/eia/:code" component={EIALookupPage} />
        <Route path="/cart" component={Cart} />
        <Route path="/seller" component={SellerDashboard} />
        <Route path="/seller/settings" component={MemberSettingsPage} />
        <Route path="/sellers/:username" component={SellerPage} />
        <Route path="/support" component={SupportPage} />
        <Route path="/admin" component={AdminHub} />
        <Route path="/admin/support" component={AdminSupportPage} />
        <Route path="/quick-start" component={QuickStartGuide} />
        <Route path="/user-guide" component={UserGuide} />
        <Route path="/about" component={AboutPage} />
        <Route path="/editorial-policy" component={EditorialPolicyPage} />
        <Route path="/terms" component={Terms} />
        <Route path="/verify/:token" component={Verify} />
        <Route path="/verify-email/:token" component={VerifyEmail} />
        <Route path="/buy-list" component={BuyListPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CartProvider>
            <WouterRouter base={BASE}>
              <RouteTracker />
              <Router />
              <AssistantLayer />
            </WouterRouter>
          </CartProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
